import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  clampRgba,
  hsvFromRgba,
  parseHex,
  rgbaEquals,
  rgbaFromHsv,
  toCssHexa,
  type Rgba,
} from '../sim/color.ts';
import { FloatingPanel, type FloatAnchor } from './floatingPanel.tsx';
import { loadRecentColors, recordRecentColor } from './settingsStore.ts';
import { setPanel } from './panels.ts';
import './colorPicker.css';

export type ColorPickerPanelProps = {
  label: string;
  value: Rgba;
  onChange: (next: Rgba) => void;
  onCommit: () => void;
  onCancel: () => void;
  anchor?: FloatAnchor;
};

export function openColorPicker(options: {
  label: string;
  value: Rgba;
  onChange: (next: Rgba) => void;
  onCommit?: () => void;
  onCancel?: () => void;
  anchor?: FloatAnchor;
}): void {
  const initial = clampRgba(options.value);
  let draft = initial;

  const close = (): void => {
    setPanel('colorPicker', null);
  };

  setPanel('colorPicker', {
    label: options.label,
    value: draft,
    anchor: options.anchor,
    onChange: (next) => {
      draft = next;
      options.onChange(next);
    },
    onCommit: () => {
      close();
      recordRecentColor(draft);
      options.onCommit?.();
    },
    onCancel: () => {
      if (!rgbaEquals(draft, initial)) {
        options.onChange(initial);
      }

      close();
      options.onCancel?.();
    },
  });
}

export function ColorPickerPanel({
  label,
  value,
  onChange,
  onCommit,
  onCancel,
  anchor,
}: ColorPickerPanelProps): JSX.Element {
  const [draft, setDraft] = useState(() => clampRgba(value));
  const [recents, setRecents] = useState(loadRecentColors);
  const hex = useRef<HTMLInputElement>(null);
  const square = useRef<HTMLDivElement>(null);
  const hsv = hsvFromRgba(draft);
  const hueColor = rgbaFromHsv({ h: hsv.h, s: 1, v: 1 }, 1);

  useEffect(() => {
    setRecents(loadRecentColors());
  }, []);

  useEffect(() => {
    const element = hex.current;

    if (element && document.activeElement !== element) {
      element.value = toCssHexa(draft);
    }
  }, [draft]);

  const emit = (next: Rgba): void => {
    const color = clampRgba(next);
    setDraft(color);
    onChange(color);
  };

  const pickFromSquare = (event: { clientX: number; clientY: number }): void => {
    const node = square.current;

    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const s = rect.width <= 0 ? 0 : Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const v = rect.height <= 0 ? 0 : Math.max(0, Math.min(1, 1 - (event.clientY - rect.top) / rect.height));
    emit(rgbaFromHsv({ h: hsv.h, s, v }, draft.a));
  };

  return (
    <FloatingPanel
      id="color-picker"
      title={label}
      class="color-picker-dialog"
      anchor={anchor}
      onDismiss={onCancel}
      onSubmit={onCommit}
      footer={
        <>
          <button type="button" class="ui-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" class="ui-button" onClick={onCommit}>
            Done
          </button>
        </>
      }
    >
      <div
        ref={square}
        class="color-picker-sv"
        style={{
          background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${toCssHexa({ ...hueColor, a: 1 })})`,
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          pickFromSquare(event);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            pickFromSquare(event);
          }
        }}
      >
        <span
          class="color-picker-sv-thumb"
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
        />
      </div>
      <div class="color-picker-slider-group">
        <div class="color-picker-hue-spectrum" aria-hidden="true" />
        <input
          type="range"
          class="color-picker-slider"
          aria-label="Hue"
          min={0}
          max={360}
          step={1}
          value={Math.round(hsv.h * 360)}
          onInput={(event) => {
            const h = Number(event.currentTarget.value) / 360;
            emit(rgbaFromHsv({ h, s: hsv.s, v: hsv.v }, draft.a));
          }}
        />
      </div>
      <div class="color-picker-slider-group">
        <span class="color-picker-slider-label">Alpha</span>
        <input
          type="range"
          class="color-picker-slider"
          aria-label="Alpha"
          min={0}
          max={1}
          step={0.01}
          value={draft.a}
          onInput={(event) => {
            const a = Number(event.currentTarget.value);

            if (Number.isFinite(a)) {
              emit({ ...draft, a });
            }
          }}
        />
      </div>
      <div class="color-picker-hex-row">
        <span class="color-picker-preview ui-color-check">
          <span class="ui-color-swatch-fill" style={{ background: toCssHexa(draft) }} />
        </span>
        <input
          ref={hex}
          type="text"
          class="color-picker-hex"
          aria-label="Hex"
          spellcheck={false}
          defaultValue={toCssHexa(draft)}
          onBlur={(event) => {
            const next = parseHex(event.currentTarget.value, draft.a);

            if (next) {
              emit(next);
            } else {
              event.currentTarget.value = toCssHexa(draft);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
        />
      </div>
      {recents.length > 0 ? (
        <div class="color-picker-recents" role="list" aria-label="Recent colours">
          {recents.map((color) => (
            <button
              key={toCssHexa(color)}
              type="button"
              class="ui-color-chip ui-color-check"
              aria-label={toCssHexa(color)}
              onClick={() => emit(color)}
            >
              <span class="ui-color-chip-fill" style={{ background: toCssHexa(color) }} />
            </button>
          ))}
        </div>
      ) : null}
    </FloatingPanel>
  );
}

/** Swatch that opens the shared floating colour pane. */
export function ColorPicker({
  label,
  value,
  onChange,
  onCommit,
  onCancel,
  mixed = false,
}: {
  label: string;
  value: Rgba;
  onChange: (next: Rgba) => void;
  onCommit?: () => void;
  onCancel?: () => void;
  mixed?: boolean;
}): JSX.Element {
  return (
    <div class="ui-control ui-control-wide">
      <span class="ui-control-label">{label}</span>
      <button
        type="button"
        class={`ui-color-swatch ui-color-check${mixed ? ' is-mixed' : ''}`}
        aria-label={label}
        aria-haspopup="dialog"
        data-color={toCssHexa(value)}
        onClick={(event) =>
          openColorPicker({
            label,
            value,
            onChange,
            onCommit,
            onCancel,
            anchor: event.currentTarget.getBoundingClientRect(),
          })
        }
      >
        {mixed ? <span class="ui-color-swatch-fill" /> : (
          <span class="ui-color-swatch-fill" style={{ background: toCssHexa(value) }} />
        )}
      </button>
    </div>
  );
}
